import { formatDate } from '@angular/common';

/**
 * Friendly, human-readable date/time formatting for Remmi AI chat results.
 *
 * The backend returns machine dates ("2026-06-20") and pre-formatted time strings
 * ("03:00 PM"). For a morning-briefing conversation we want readable, relative wording
 * instead — "Today, 3:00 PM" / "Tomorrow, 9:00 AM" / "Thu 20 Jun, 3:00 PM".
 *
 * Pure helpers (no Angular DI) so they can be reused from components and the
 * {@link AiFriendlyDatePipe}.
 */

const LOCALE = 'en-AU';

/** Parse a backend date string to a local Date. Date-only strings are parsed in local time
 *  (so the calendar day never shifts across a UTC boundary). Returns null if unparseable. */
function parseDate(value?: string | null): Date | null {
  const raw = (value || '').trim();
  if (!raw) {
    return null;
  }
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  }
  const parsed = new Date(raw);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/** Whole-day difference between two dates, ignoring the time component. */
function dayDiff(target: Date, from: Date): number {
  const a = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
  const b = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  return Math.round((a - b) / 86_400_000);
}

/**
 * Friendly day label: "Today" / "Tomorrow" / "Yesterday", otherwise "Thu 20 Jun"
 * (the year is appended only when it differs from the current year).
 * Falls back to the original string if it can't be parsed.
 */
export function friendlyDay(dateValue?: string | null): string {
  const date = parseDate(dateValue);
  if (!date) {
    return (dateValue || '').trim();
  }
  const diff = dayDiff(date, new Date());
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return formatDate(date, sameYear ? 'EEE d MMM' : 'EEE d MMM y', LOCALE);
}

/** Tidy a backend time string for display, e.g. "03:00 PM" → "3:00 PM". */
export function cleanTime(time?: string | null): string {
  return (time || '').trim().replace(/^0(\d:)/, '$1');
}

/**
 * Friendly date + optional time, e.g. "Today, 3:00 PM" / "Thu 20 Jun, 9:00 AM".
 * When no time is supplied only the day label is returned.
 */
export function friendlyDateTime(dateValue?: string | null, time?: string | null): string {
  return [friendlyDay(dateValue), cleanTime(time)].filter(Boolean).join(', ');
}

/**
 * Friendly date with a time range, e.g. "Thu 20 Jun, 9:00 AM – 9:30 AM".
 * Used for appointment slots (start/end). Either time may be empty.
 */
export function friendlyDateTimeRange(dateValue?: string | null, start?: string | null, end?: string | null): string {
  const range = [cleanTime(start), cleanTime(end)].filter(Boolean).join(' – ');
  return [friendlyDay(dateValue), range].filter(Boolean).join(', ');
}

/**
 * Friendly label for a single ISO timestamp (e.g. a conversation's `updated_at`):
 * "Today, 3:00 PM" / "Yesterday, 9:12 AM" / "Thu 20 Jun, 4:05 PM". The time is included only
 * when the value carries one.
 */
export function friendlyTimestamp(value?: string | null): string {
  const date = parseDate(value);
  if (!date) {
    return (value || '').trim();
  }
  const day = friendlyDay(value);
  const hasTime = /\d{1,2}:\d{2}/.test(value || '');
  return hasTime ? `${day}, ${formatDate(date, 'h:mm a', LOCALE)}` : day;
}
